import React, { useEffect, useState, useRef, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { topics, BlogPost } from "../../data/blog/blog";
import { useBlogPosts } from "../../hooks/useBlogPosts";

interface TableOfContents {
  id: string;
  text: string;
  level: number;
}

const BlogDetail = () => {
  const { title } = useParams<{ title: string }>();
  const navigate = useNavigate();
  const { posts: allPosts, loading } = useBlogPosts();
  const [activeHeading, setActiveHeading] =
    useState<string>("");
  const [tableOfContents, setTableOfContents] = useState<
    TableOfContents[]
  >([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestedMode, setSuggestedMode] = useState(0); // 0: Recently Viewed, 1: Latest, 2: Most Popular
  const [searchActive, setSearchActive] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Find the post by title or slug
  const post = useMemo(() => {
    return allPosts.find(
      (p) =>
        p.slug === title ||
        p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") === title
    );
  }, [allPosts, title]);

  // If post not found and not loading, redirect to blog page
  useEffect(() => {
    if (!loading && !post) {
      navigate("/blog");
    }
  }, [post, loading, navigate]);

  // Generate table of contents from content
  useEffect(() => {
    if (post?.content) {
      const headings =
        post.content.match(
          /<h[2-3][^>]*>(.*?)<\/h[2-3]>/g
        ) || [];
      const toc = headings.map((heading, index) => {
        const level = parseInt(heading.charAt(2));
        const title = heading.replace(/<[^>]+>/g, "");
        const id = `heading-${index}`;
        return { id, text: title, level };
      });
      setTableOfContents(toc);
    }
  }, [post]);

  // Update active heading on scroll with smooth scrolling
  useEffect(() => {
    const handleScroll = () => {
      const headings = document.querySelectorAll("h2, h3");
      let currentHeading = "";

      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 100) {
          currentHeading = heading.id;
        }
      });

      setActiveHeading(currentHeading);
    };

    window.addEventListener("scroll", handleScroll);
    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll to heading
  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition =
        element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Handle image click for lightbox
  const handleImageClick = (
    e: React.MouseEvent<HTMLImageElement>
  ) => {
    const img = e.target as HTMLImageElement;
    const lightbox = document.createElement("div");
    lightbox.className =
      "fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center cursor-zoom-out";
    lightbox.onclick = () => lightbox.remove();

    const fullImage = document.createElement("img");
    fullImage.src = img.src;
    fullImage.alt = img.alt;
    fullImage.className =
      "max-w-[90%] max-h-[90vh] object-contain";

    lightbox.appendChild(fullImage);
    document.body.appendChild(lightbox);
  };

  // Search logic
  const searchResults = allPosts.filter(
    (p) =>
      p.id !== post?.id &&
      (p.title
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
        p.keyword
          .toLowerCase()
          .includes(searchQuery.toLowerCase()))
  );

  // Recently viewed logic (localStorage)
  useEffect(() => {
    if (post) {
      let viewed = JSON.parse(
        localStorage.getItem("recentlyViewed") || "[]"
      );
      viewed = viewed.filter(
        (id: number) => id !== post.id
      );
      viewed.unshift(post.id);
      if (viewed.length > 10) viewed = viewed.slice(0, 10);
      localStorage.setItem(
        "recentlyViewed",
        JSON.stringify(viewed)
      );
    }
  }, [post]);

  // Get the 3 most recently viewed articles (excluding current), in correct order
  const recentlyViewedIds = (
    JSON.parse(
      localStorage.getItem("recentlyViewed") || "[]"
    ) as number[]
  ).filter((id) => id !== post?.id);
  const recentlyViewed = recentlyViewedIds
    .map((id) => allPosts.find((p) => p.id === id))
    .filter((p): p is BlogPost => !!p)
    .slice(0, 3);

  // Latest articles (by date, excluding current)
  const latestArticles = allPosts
    .filter((p) => p.id !== post?.id)
    .sort((a, b) => {
      const parse = (d: string) => {
        const [day, month, year] = d.split("/").map(Number);
        return new Date(year, month - 1, day).getTime();
      };
      return parse(b.date) - parse(a.date);
    })
    .slice(0, 3);

  // Most popular (featured) articles, excluding current
  const popularArticles = allPosts
    .filter((p) => p.id !== post?.id && p.featured)
    .slice(0, 3);

  const suggestedArticles =
    suggestedMode === 0
      ? recentlyViewed
      : suggestedMode === 1
      ? latestArticles
      : popularArticles;

  // Close search dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setSearchActive(false);
      }
    }
    document.addEventListener(
      "mousedown",
      handleClickOutside
    );
    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] dark:bg-black flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Post Not Found - VEA Group Blog</title>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="icon" href="/favicon.ico" />
        </Helmet>
        <div className="min-h-screen bg-[#fafafa] dark:bg-black py-16 px-4 md:px-8">
          <div className="container mx-auto text-center">
            <h1 className="text-2xl font-bold text-black dark:text-white">
              Post not found
            </h1>
            <button
              onClick={() => navigate("/blog")}
              className="mt-4 px-6 py-2 bg-primary text-white rounded-full hover:bg-opacity-90 transition-all"
            >
              Back to Blog
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} - VEA Group Blog</title>
        <meta name="description" content={post.desc} />
        <meta
          name="keywords"
          content={`${post.topic}, blog, VEA Group`}
        />
        <link
          rel="canonical"
          href={`https://veagroup.global/blog/${post.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")}`}
        />
        <link rel="icon" href="/favicon.ico" />
        <link rel="manifest" href="/manifest.json" />
        {/* Open Graph */}
        <meta
          property="og:title"
          content={`${post.title} - VEA Group Blog`}
        />
        <meta
          property="og:description"
          content={post.desc}
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://veagroup.global/blog/${post.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")}`}
        />
        <meta
          property="og:image"
          content={post.image || "/logo.png"}
        />
        {/* Twitter Card */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content={`${post.title} - VEA Group Blog`}
        />
        <meta
          name="twitter:description"
          content={post.desc}
        />
        <meta
          name="twitter:image"
          content={post.image || "/logo.png"}
        />
      </Helmet>
      <div className="min-h-screen bg-[#fafafa] dark:bg-black py-16 pt-40 px-4 md:px-8">
        <div className="container mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate("/blog")}
            className="mb-8 flex items-center text-primary hover:text-opacity-80 transition-all group"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2 transform group-hover:-translate-x-1 transition-transform"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back to Blog
          </button>

          {/* Two Column Layout */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content - 3/5 width */}
            <div className="lg:w-3/5">
              {/* Cover Photo */}
              <div className="relative w-full h-[400px] rounded-xl overflow-hidden mb-6">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-20"></div>
              </div>

              {/* Article Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-black dark:text-white mb-4">
                {post.title}
              </h1>

              {/* Info Section */}
              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {post.date}
                </div>
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {post.time}
                </div>
              </div>

              {/* Table of Contents */}
              {tableOfContents.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow p-4 mb-8">
                  <h2 className="text-lg font-bold text-black dark:text-white mb-3">
                    Table of Contents
                  </h2>
                  <nav className="space-y-2">
                    {tableOfContents.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToHeading(item.id);
                        }}
                        className={`block text-sm transition-colors duration-200 ${
                          activeHeading === item.id
                            ? "text-primary font-medium"
                            : "text-gray-600 dark:text-gray-400 hover:text-primary"
                        } ${
                          item.level === 3 ? "pl-4" : ""
                        }`}
                      >
                        {item.text}
                      </a>
                    ))}
                  </nav>
                </div>
              )}

              {/* Blog Content */}
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-8">
                <div
                  className="prose dark:prose-invert max-w-none"
                  ref={contentRef}
                >
                  {post.content ? (
                    <div
                      className="blog-content text-gray-600 dark:text-gray-300"
                      dangerouslySetInnerHTML={{
                        __html: (() => {
                          // Inject IDs into headings to match TOC
                          let html = post.content;
                          const headingRegex =
                            /<h([23])([^>]*)>(.*?)<\/h[23]>/g;
                          let idx = 0;
                          html = html.replace(
                            headingRegex,
                            (match, level, attrs, text) => {
                              const id = `heading-${idx++}`;
                              return `<h${level} id="${id}"${attrs}>${text}</h${level}>`;
                            }
                          );
                          return html
                            .replace(
                              /<h2/g,
                              '<h2 class="text-2xl font-bold mt-8 mb-4 scroll-mt-24"'
                            )
                            .replace(
                              /<h3/g,
                              '<h3 class="text-xl font-bold mt-6 mb-3 scroll-mt-24"'
                            )
                            .replace(
                              /<p/g,
                              '<p class="mb-4 leading-relaxed"'
                            )
                            .replace(
                              /<img/g,
                              '<img class="rounded-lg my-6 cursor-zoom-in hover:opacity-90 transition-opacity"'
                            )
                            .replace(
                              /<ul/g,
                              '<ul class="list-disc pl-6 mb-4 space-y-2"'
                            )
                            .replace(
                              /<ol/g,
                              '<ol class="list-decimal pl-6 mb-4 space-y-2"'
                            )
                            .replace(
                              /<li/g,
                              '<li class="mb-2"'
                            )
                            .replace(
                              /<blockquote/g,
                              '<blockquote class="border-l-4 border-primary pl-4 my-6 italic bg-gray-50 dark:bg-gray-800 p-4 rounded-r-lg"'
                            )
                            .replace(
                              /<code/g,
                              '<code class="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm font-mono"'
                            )
                            .replace(
                              /<pre/g,
                              '<pre class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg my-6 overflow-x-auto"'
                            )
                            .replace(
                              /<a/g,
                              '<a class="text-primary hover:text-opacity-80 underline"'
                            )
                            .replace(
                              /<table/g,
                              '<table class="w-full border-collapse my-6"'
                            )
                            .replace(
                              /<th/g,
                              '<th class="border border-gray-300 dark:border-gray-700 px-4 py-2 bg-gray-100 dark:bg-gray-800"'
                            )
                            .replace(
                              /<td/g,
                              '<td class="border border-gray-300 dark:border-gray-700 px-4 py-2"'
                            );
                        })(),
                      }}
                      onClick={(e) => {
                        const target =
                          e.target as HTMLElement;
                        if (target.tagName === "IMG") {
                          handleImageClick(
                            e as React.MouseEvent<HTMLImageElement>
                          );
                        }
                      }}
                    />
                  ) : (
                    <p className="text-gray-600 dark:text-gray-300">
                      Content coming soon...
                    </p>
                  )}
                </div>
              </div>

              {/* Faint Line */}
              <hr className="my-8 border-t border-gray-200 dark:border-gray-700 opacity-60" />

              {/* Info Bar: Topic and Tags/Keywords */}
              <div className="flex items-center gap-8 text-gray-500 dark:text-gray-400 text-base mt-4">
                {/* Topic/Category */}
                <div className="flex items-center gap-1">
                  <svg
                    className="w-5 h-5 mr-1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 7a2 2 0 012-2h3.28a2 2 0 011.42.59l1.42 1.42A2 2 0 0012.72 8H19a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                    />
                  </svg>
                  <span>{post.topic}</span>
                </div>
                {/* Tags/Keywords */}
                <div className="flex items-center gap-1">
                  <svg
                    className="w-5 h-5 mr-1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7 7a1 1 0 011-1h3.586a1 1 0 01.707.293l7.414 7.414a2 2 0 010 2.828l-4.586 4.586a2 2 0 01-2.828 0l-7.414-7.414A1 1 0 013 10.586V7a1 1 0 011-1h3z"
                    />
                  </svg>
                  {post.keyword
                    .split(",")
                    .map((tag, idx, arr) => (
                      <span
                        key={idx}
                        className="inline-block"
                      >
                        {tag.trim()}
                        {idx < arr.length - 1 && (
                          <span className="mx-1">,</span>
                        )}
                      </span>
                    ))}
                </div>
              </div>
            </div>

            {/* Widget Bar - 2/5 width */}
            <div className="lg:w-2/5 space-y-8">
              {/* 1. Search Bar */}
              <div
                className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 mb-4"
                ref={searchRef}
              >
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line
                        x1="21"
                        y1="21"
                        x2="16.65"
                        y2="16.65"
                      />
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Search articles..."
                    className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-sm"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSearchActive(true);
                    }}
                    onFocus={() => setSearchActive(true)}
                  />
                  {searchQuery && searchActive && (
                    <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-10 max-h-64 overflow-y-auto">
                      {searchResults.length === 0 ? (
                        <div className="text-gray-400 text-sm px-4 py-3">
                          No results found.
                        </div>
                      ) : (
                        searchResults.map((result) => (
                          <div
                            key={result.id}
                            className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                            onClick={() => {
                              setSearchActive(false);
                              navigate(
                                `/blog/${result.title
                                  .toLowerCase()
                                  .replace(
                                    /[^a-z0-9]+/g,
                                    "-"
                                  )}`
                              );
                            }}
                          >
                            <img
                              src={result.image}
                              alt={result.title}
                              className="w-10 h-10 object-cover rounded-lg flex-shrink-0 border border-gray-200 dark:border-gray-700"
                            />
                            <span className="text-gray-800 dark:text-gray-100 text-sm font-medium line-clamp-1">
                              {result.title}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 2. Suggested Article Modes */}
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 mb-4">
                <div className="flex gap-2 mb-4">
                  {[
                    "Recently Viewed",
                    "Latest",
                    "Most Popular",
                  ].map((mode, idx) => (
                    <button
                      key={mode}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                        suggestedMode === idx
                          ? "bg-primary text-white"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-white"
                      }`}
                      onClick={() => setSuggestedMode(idx)}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
                <div className="space-y-4">
                  {suggestedArticles.map((article) => (
                    <div
                      key={article.id}
                      className="flex gap-4 cursor-pointer group"
                      onClick={() =>
                        navigate(
                          `/blog/${article.title
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`
                        )
                      }
                    >
                      <div className="w-16 h-16 flex-shrink-0">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-black dark:text-white group-hover:text-primary transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-xs line-clamp-2">
                          {article.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 3. Tags Section */}
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 mb-4">
                <h2 className="text-lg font-bold text-black dark:text-white mb-3">
                  Tags
                </h2>
                <div className="flex flex-wrap gap-2">
                  {topics
                    .filter((t) => t !== "All Topics")
                    .map((topic) => (
                      <button
                        key={topic}
                        className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-white text-sm"
                        onClick={() =>
                          navigate(
                            `/blog?topic=${encodeURIComponent(
                              topic
                            )}`
                          )
                        }
                      >
                        {topic}
                      </button>
                    ))}
                </div>
              </div>

              {/* 4. Keywords Section */}
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6">
                <h2 className="text-lg font-bold text-black dark:text-white mb-3">
                  Related Keywords
                </h2>
                <div className="flex flex-wrap gap-2">
                  {post.keyword
                    .split(",")
                    .map((keyword) => (
                      <button
                        key={keyword.trim()}
                        className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-white text-sm"
                        onClick={() =>
                          navigate(
                            `/blog?keyword=${encodeURIComponent(
                              keyword.trim()
                            )}`
                          )
                        }
                      >
                        {keyword.trim()}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetail;
